import React from 'react';
import { ArrowUpRight } from 'lucide-react';

import cinnamon from "../../assets/cinnomon.webp";
import pepper from "../../assets/pepper.webp";
import tea from "../../assets/tea.webp";
import cloves from "../../assets/cloves.webp";
import coconuts from "../../assets/coconut.webp";
import coconutProducts from "../../assets/coconut-products.webp";
import spices from "../../assets/spices.webp";
import cardamom from "../../assets/cardamom.webp";
import arecanut from "../../assets/areacanut.webp";
import grocery from "../../assets/grocery.webp";
import rubber from "../../assets/rubber.webp";

const CommodityServices = () => {

  const products = [
    {
      name: "Ceylon Black Pepper",
      desc: "High-piperine Sri Lankan black pepper sourced directly from certified plantations, offering bold aroma, uniform grading, and export-ready purity.",
      image: pepper,
      tag: "Spice"
    },
    {
      name: "Ceylon Cinnamon",
      desc: "Premium Alba & C5 grade true cinnamon with superior oil content, sweet aroma, and strict quality compliance for global markets.",
      image: cinnamon,
      tag: "Herb"
    },
    {
      name: "Ceylon Tea",
      desc: "Single-origin orthodox Ceylon tea from high-grown estates, known for its rich flavor profile, clarity, and consistency.",
      image: tea,
      tag: "Tea"
    },
    {
      name: "Ceylon Cloves",
      desc: "Premium Sri Lankan cloves with rich oil content, intense aroma, and export-grade quality.",
      image: cloves,
      tag: "Herb"
    },
    {
      name: "Ceylon Coconuts",
      desc: "Fresh Sri Lankan coconuts sourced from sustainable plantations and processed for global export.",
      image: coconuts,
      tag: "Coconut"
    },
    {
      name: "Ceylon Coconut Products",
      desc: "High-quality coconut-based products including oil, milk, and desiccated coconut.",
      image: coconutProducts,
      tag: "Coconut Products"
    },
    {
      name: "Ceylon Spices",
      desc: "A diverse range of premium Sri Lankan spices meeting international quality standards.",
      image: spices,
      tag: "Spices"
    },
    {
      name: "Ceylon Cardamom",
      desc: "Aromatic Sri Lankan cardamom with superior flavor and essential oil content.",
      image: cardamom,
      tag: "Spices"
    },
    {
      name: "Ceylon Nut Meg",
      desc: "Carefully processed Nut Meg suitable for food and industrial applications.",
      image: arecanut,
      tag: "Spices"
    },
    {
      name: "Ceylon Grocery Products",
      desc: "A curated range of Sri Lankan grocery products for global markets.",
      image: grocery,
      tag: "Grocery"
    },
    {
      name: "Ceylon Rubber Products",
      desc: "High-grade Sri Lankan rubber products with consistent quality and durability.",
      image: rubber,
      tag: "Rubber"
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] overflow-hidden">

      {/* Header */}
      <section className="relative bg-white pt-24 pb-16 border-b border-blue-100">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(#1e40af 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
            <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
            <span className="text-xs font-bold text-blue-700 uppercase tracking-widest">
              Export Products
            </span>
          </div>

          <h2 className="text-5xl font-black text-slate-900 mb-6 tracking-tighter">
            Our <span className="text-blue-600">Commodities</span>
          </h2>

          <p className="text-xl text-slate-500 max-w-2xl leading-relaxed">
            Delivering premium Sri Lankan agricultural commodities through transparent sourcing,
            strict quality assurance, and global logistics expertise.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24 relative">
        <div className="absolute left-4 top-1/2 -rotate-90 hidden lg:block text-[10rem] font-black text-slate-200/40 select-none pointer-events-none">
          PRODUCTS
        </div>

        <div className="max-w-7xl mx-auto px-6 space-y-16">
          {products.map((product, index) => (
            <div
              key={index}
              className={`group flex flex-col md:flex-row gap-8 items-stretch ${
                index % 2 !== 0 ? 'md:flex-row-reverse' : ''
              }`}
            >

              {/* Image Block */}
              <div className="md:w-1/3 bg-white rounded-3xl overflow-hidden shadow-xl shadow-blue-900/5 border border-blue-50 relative">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent"></div>

                <div className="absolute bottom-6 left-6">
                  <span className="text-xs font-bold bg-blue-600 text-white px-3 py-1 rounded-full uppercase tracking-widest">
                    {product.tag}
                  </span>
                </div>
              </div>

              {/* Content Block */}
              <div className="md:w-2/3 bg-white/50 backdrop-blur-md rounded-3xl p-10 border border-white hover:border-blue-200 hover:bg-white transition-all duration-500 flex flex-col justify-center shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] font-black bg-slate-900 text-white px-2 py-1 rounded">
                    EXPORT
                  </span>
                  <div className="h-[1px] flex-grow bg-slate-200"></div>
                </div>

                <h3 className="text-3xl font-bold text-slate-800 mb-4 flex justify-between items-center transition-colors duration-300 group-hover:text-blue-600">
                  {product.name}
                  <ArrowUpRight className="text-slate-300 transition-colors duration-300 group-hover:text-blue-500" />
                </h3>

                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  {product.desc}
                </p>

                <div className="flex gap-4">
                  <div className="h-1 w-12 bg-blue-600 rounded-full"></div>
                  <div className="h-1 w-4 bg-blue-200 rounded-full"></div>
                  <div className="h-1 w-2 bg-blue-100 rounded-full"></div>
                </div>
              </div>

            </div>
          ))}
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { label: "Partner Farms", val: "120+" },
            { label: "Export Countries", val: "35+" },
            { label: "Quality Compliance", val: "100%" },
            { label: "Annual Capacity", val: "15,000 MT" }
          ].map((stat, i) => (
            <div key={i}>
              <div className="text-blue-400 font-mono text-xs mb-1 uppercase tracking-widest">
                {stat.label}
              </div>
              <div className="text-white text-3xl font-black">
                {stat.val}
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default CommodityServices;
